/**
 * BillingCheckout — iOS subscription payment page
 * Opened from mobile iOS via Linking.openURL (APPLE-3.1.1-FIX: native checkout
 * not permitted on iOS for subscription billing).
 *
 * Loads Razorpay Checkout.js, auto-opens the modal, then shows the proven
 * PaymentPending.js return-to-app pattern on success.
 *
 * UX signal only: the JS handler confirms mandate setup to the user.
 * Plan activation authority is webhook (subscription.activated event) — NOT here.
 *
 * Query params: ?subscriptionId=xxx&keyId=xxx&plan=xxx
 * No auth required — opened from an unauthenticated iOS Safari session.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';

export default function BillingCheckout() {
  const params         = new URLSearchParams(window.location.search);
  const subscriptionId = params.get('subscriptionId') || '';
  const keyId          = params.get('keyId') || '';
  const plan           = params.get('plan') || '';

  const [uiState,  setUiState]  = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const successRef              = useRef(false);

  const openRazorpay = useCallback(() => {
    successRef.current = false;
    setUiState('checkout');
    setErrorMsg('');

    const options = {
      key:             keyId,
      subscription_id: subscriptionId,
      name:            'LifeChiAI',
      description:     plan ? `${plan} plan` : 'Subscription',
      theme:           { color: '#2ABFAB' },
      handler: function () {
        // Mandate setup confirmed by Razorpay SDK.
        // Activation authority is webhook (subscription.activated) — not this callback.
        successRef.current = true;
        setUiState('success');
      },
      modal: {
        ondismiss: function () {
          if (!successRef.current) {
            setUiState('error');
            setErrorMsg('Payment was not completed. Return to the app and try again from the subscription screen.');
          }
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function () {
      setUiState('error');
      setErrorMsg('Payment failed. Please return to the app and try again.');
    });
    rzp.open();
  }, [keyId, subscriptionId, plan]);

  useEffect(() => {
    if (!subscriptionId || !keyId) {
      setUiState('error');
      setErrorMsg('Missing payment parameters. Please return to the app and try again.');
      return;
    }

    if (window.Razorpay) { openRazorpay(); return; }

    const script   = document.createElement('script');
    script.src     = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async   = true;
    script.onload  = openRazorpay;
    script.onerror = () => {
      setUiState('error');
      setErrorMsg('Could not load the payment gateway. Check your connection and try again.');
    };
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch {} };
  }, [openRazorpay, subscriptionId, keyId]);

  if (uiState === 'success') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.iconWrap}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 18L15 26L29 10" stroke="#2ABFAB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2 style={s.heading}>Payment Received</h2>
          <p style={s.body}>Your subscription is being activated. Tap the button below to return to the app.</p>
          <button style={s.btn} onClick={() => { window.location.href = 'lifechiai://payment-complete'; }}>
            Tap here to return to LifeChiAI
          </button>
          <p style={s.fallback}>If the app does not open, please launch it manually — your subscription will be active within a few minutes.</p>
        </div>
      </div>
    );
  }

  if (uiState === 'error') {
    const canRetry = !!(subscriptionId && keyId && window.Razorpay);
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.iconWrap}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 10V20" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" />
              <circle cx="18" cy="27" r="2" fill="#EF4444" />
            </svg>
          </div>
          <h2 style={{ ...s.heading, color: '#EF4444' }}>Payment Incomplete</h2>
          <p style={s.body}>{errorMsg || 'Something went wrong. Please return to the app and try again.'}</p>
          {canRetry && (
            <button style={s.btn} onClick={openRazorpay}>Try Again</button>
          )}
          <p style={s.fallback}>If you need help, contact support from within the LifeChiAI app.</p>
        </div>
      </div>
    );
  }

  // loading + checkout: minimal backdrop while Razorpay modal is loading or open
  return (
    <div style={s.page}>
      <div style={s.card}>
        <p style={s.body}>
          {uiState === 'loading' ? 'Loading payment gateway…' : 'Complete your payment in the checkout window.'}
        </p>
      </div>
    </div>
  );
}

const s = {
  page:     { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0F8FF', padding: '24px' },
  card:     { backgroundColor: '#fff', borderRadius: '16px', padding: '40px 32px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  iconWrap: { width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#E6F9F5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
  heading:  { fontSize: '22px', fontWeight: '700', color: '#0D1F1A', marginBottom: '12px' },
  body:     { fontSize: '15px', color: '#4A6B63', lineHeight: '1.6', marginBottom: '24px' },
  btn:      { display: 'inline-block', padding: '14px 32px', borderRadius: '10px', backgroundColor: '#2ABFAB', color: '#fff', fontSize: '15px', fontWeight: '600', border: 'none', cursor: 'pointer', minHeight: '44px', marginBottom: '16px' },
  fallback: { fontSize: '13px', color: '#8A9E98', lineHeight: '1.5' },
};
